import { useState, useCallback, useRef } from 'react';
import { client } from '../../services/api';
import { isAbortError } from '../../utils/abortableRequest';
import { buildKinnserMatchRequestBody } from './kinnserMatch';
import type { PatientMatchResult } from './types';

interface UsePatientMatchingOptions {
  /** Kinnser clinic_key used for RogerDoc patient match API. */
  clinicKey?: string | null;
  /** Agency clinic UUID from intake — required for Roger match. */
  agencyClinicId?: string | null;
  /** Roger `patients.id` to exclude (e.g. intake already linked after onboard). */
  excludePatientId?: string | null;
  /** Agency setting: is_kinnser_enabled */
  kinnserIntakeEnabled?: boolean;
  /** Agency setting: is_rogeremr_enabled */
  rogerIntakeEnabled?: boolean;
  /**
   * EMR-376: when set, only run that EMR's duplicate check.
   * `undefined` = legacy auto (Kinnser wins when both eligible).
   */
  preferredEmr?: 'roger' | 'kinnser';
}

interface PatientMatchData {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  mrn?: string | null;
  dob?: string | null;
  ssn?: string | null;
  phone?: string | null;
  address?: string | null;
  zipCode?: string | null;
  gender?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
}

interface PatientMatchOptions {
  includeEpisodeInfo?: boolean;
  /** When false, do not open the modal when matches are found (e.g. when patient already selected, prepopulate matches but only open on "View potential matches"). Default true. */
  openModalOnMatches?: boolean;
}

interface UsePatientMatchingReturn {
  patientMatches: PatientMatchResult[];
  isMatchingPatients: boolean;
  patientMatchModalOpen: boolean;
  setPatientMatchModalOpen: (open: boolean) => void;
  checkPatientMatch: (
    patientData: PatientMatchData,
    options?: PatientMatchOptions
  ) => Promise<void>;
  resetMatches: () => void;
  /** True when at least one duplicate-check backend is configured (agency EMR + clinic ids). */
  duplicateCheckReady: boolean;
}

/**
 * Duplicate patient checks: Roger EMR (POST /patients/match) and/or Kinnser (RogerDoc).
 *
 * Gating:
 *   - Kinnser branch runs when agency.is_kinnser_enabled AND a clinic_key exists.
 *   - Roger branch runs when agency.is_rogeremr_enabled AND an agency_clinic_id exists.
 *
 * When both branches would be eligible and preferredEmr is unset, only the Kinnser
 * branch runs. preferredEmr (EMR-376) forces a single path when set.
 */
export function usePatientMatching({
  clinicKey,
  agencyClinicId,
  excludePatientId,
  kinnserIntakeEnabled = false,
  rogerIntakeEnabled = false,
  preferredEmr,
}: UsePatientMatchingOptions): UsePatientMatchingReturn {
  const [patientMatches, setPatientMatches] = useState<PatientMatchResult[]>(
    []
  );
  const [isMatchingPatients, setIsMatchingPatients] = useState(false);
  const [patientMatchModalOpen, setPatientMatchModalOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const ck = (clinicKey ?? '').toString().trim();
  const aid = (agencyClinicId ?? '').toString().trim();
  const excludePid = (excludePatientId ?? '').toString().trim() || null;
  const kinnserEligible = kinnserIntakeEnabled && !!ck;
  const rogerEligible = rogerIntakeEnabled && !!aid;

  let runKinnser = kinnserEligible;
  let runRoger = rogerEligible && !kinnserEligible;
  if (preferredEmr === 'roger') {
    runKinnser = false;
    runRoger = rogerEligible;
  } else if (preferredEmr === 'kinnser') {
    runRoger = false;
    runKinnser = kinnserEligible;
  }

  const duplicateCheckReady = runKinnser || runRoger;

  const checkPatientMatch = useCallback(
    async (patientData: PatientMatchData, options?: PatientMatchOptions) => {
      const firstName = patientData.firstName.trim();
      const lastName = patientData.lastName.trim();
      const openModalOnMatches = options?.openModalOnMatches !== false;

      if (!runRoger && !runKinnser) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const currentController = new AbortController();
      abortControllerRef.current = currentController;
      const signal = currentController.signal;

      try {
        setIsMatchingPatients(true);

        const jobs: Array<{
          source: 'roger_emr' | 'kinnser';
          label: string;
          req: ReturnType<typeof client.request>;
        }> = [];

        if (runRoger) {
          jobs.push({
            source: 'roger_emr',
            label: 'Roger',
            req: client.request({
              method: 'POST',
              url: '/patients/match',
              body: {
                agency_clinic_id: aid,
                first_name: firstName,
                last_name: lastName,
                date_of_birth: patientData.dob?.trim() || null,
                ssn: patientData.ssn?.trim() || null,
                phone: patientData.phone?.trim() || null,
                address: patientData.address?.trim() || null,
                zip_code: patientData.zipCode?.trim() || null,
                gender: patientData.gender?.trim() || null,
                ...(excludePid ? { exclude_patient_id: excludePid } : {}),
              },
              signal,
            }),
          });
        }

        if (runKinnser) {
          jobs.push({
            source: 'kinnser',
            label: 'WellSky',
            req: client.request({
              method: 'POST',
              url: `/rogerdoc/api/patient/match/${ck}`,
              query: { include_episode_info: 'true' },
              body: buildKinnserMatchRequestBody({
                firstName,
                middleName: patientData.middleName,
                lastName,
                mrn: patientData.mrn,
                dob: patientData.dob,
                ssn: patientData.ssn,
                address: patientData.address,
                zipCode: patientData.zipCode,
                gender: patientData.gender,
                email: patientData.email,
                city: patientData.city,
                state: patientData.state,
              }),
              signal,
            }),
          });
        }

        const settled = await Promise.allSettled(jobs.map(j => j.req));
        const merged: PatientMatchResult[] = [];
        settled.forEach((result, i) => {
          const { source, label } = jobs[i];
          if (result.status === 'fulfilled') {
            const rows = result.value.data as PatientMatchResult[];
            merged.push(...rows.map(r => ({ ...r, match_source: source })));
          } else if (!isAbortError(result.reason)) {
            console.error(`${label} patient match error:`, result.reason);
          }
        });

        setPatientMatches(merged);
        if (openModalOnMatches && merged.length > 0) {
          setPatientMatchModalOpen(true);
        }
      } catch (error: unknown) {
        if (!isAbortError(error)) {
          console.error('Patient match error:', error);
        }
      } finally {
        if (abortControllerRef.current === currentController) {
          setIsMatchingPatients(false);
        }
      }
    },
    [aid, ck, excludePid, runKinnser, runRoger]
  );

  const resetMatches = useCallback(() => {
    setPatientMatches([]);
    setPatientMatchModalOpen(false);
    setIsMatchingPatients(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    patientMatches,
    isMatchingPatients,
    patientMatchModalOpen,
    setPatientMatchModalOpen,
    checkPatientMatch,
    resetMatches,
    duplicateCheckReady,
  };
}
